import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type {
  LvDokument, LvGesamt, LvGrundlagen, LvKopf, LvLauf, LvPosition,
  LvPositionselement, LvTextbaustein, LvTitel, LvVorbemerkung,
} from '../api';
import type { ApiClient } from '../api';
import type { Rahmen } from './Projektrahmen';
import {
  IconBuch, IconEbene, IconHerunterladen, IconOk, IconSuche,
  IconTabelle, IconWarnung,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, EmptyState, ErrorState, StatusBadge, type Spalte, type Zustand,
} from '../muster';

export const GEWERKE: Record<string, { name: string; pfad: string }> = {
  lueftung: { name: 'Lüftung', pfad: 'lueftung' },
  heizung: { name: 'Heizung und Kälte', pfad: 'heizung-kaelte' },
  sanitaer: { name: 'Sanitär', pfad: 'sanitaer' },
  sprinkler: { name: 'Sprinkler', pfad: 'sprinkler' },
  elektro: { name: 'Elektro', pfad: 'elektro' },
  msr: { name: 'Gebäudeautomation (MSR)', pfad: 'msr' },
  aussenanlagen: { name: 'Außenanlagen', pfad: 'aussenanlagen' },
};

/**
 * Was die Mengenherkunft in Worten heißt — und welchen Zustand sie trägt.
 *
 * Umzug U6c: `zustand` statt einer nackten Farbe. Bis U1–U3 war `--ok` gar
 * nicht definiert, die Farbe griff also an sechs Stellen dieser Datei nie;
 * seitdem greift sie, war aber weiterhin FARBE ALLEIN. `StatusBadge` trägt
 * Zeichen und Text (Auftrag Abschnitt 18/28).
 */
const HERKUNFT: Record<string, { text: string; zustand: Zustand }> = {
  modell: { text: 'aus dem Modell', zustand: 'gut' },
  kataster: { text: 'aus dem Kataster', zustand: 'gut' },
  berechnung: { text: 'gerechnet', zustand: 'info' },
  festlegung: { text: 'Festlegung', zustand: 'warnung' },
  pauschal: { text: 'pauschal', zustand: 'warnung' },
};

const POSITIONSART: Record<string, string> = {
  normal: '',
  grund: 'Grundposition',
  alternativ: 'Alternativposition',
  bedarf: 'Bedarfsposition',
  zulage: 'Zulageposition',
  stundenlohn: 'Stundenlohn',
  hinweis: 'Hinweistext',
};

/**
 * Ausschreibung je Gewerk — das LV-Modul.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS DIESE SEITE ZEIGT UND WARUM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Links der Baum Los → Titel → Position, rechts die gewählte Position mit
 * ihrem vollständigen Langtext. Das ist die Ansicht, in der ein Fachplaner
 * ein LV durchgeht: Er sucht eine Position, liest den Text und prüft die
 * Menge.
 *
 * An JEDER Position steht die MENGENHERKUNFT. Sie ist nach VOB/A § 7 der
 * Nachweis, ob eine Zahl gezählt, gerechnet oder festgelegt wurde — und sie
 * stand bis heute in keiner Ausgabe, weder im PDF noch im GAEB noch auf dem
 * Bildschirm.
 *
 * Die MODELLELEMENTE werden erst auf Klick geladen. Eine einzige Position
 * kann über 4.900 Bauteile tragen; die wandern nicht durch die Leitung, weil
 * jemand einen Titel aufklappt.
 */
export function Ausschreibung() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { gewerk = 'lueftung' } = useParams<{ gewerk: string }>();
  const fach = GEWERKE[gewerk];

  const [reiter, setReiter] = useState<'lv' | 'grundlagen' | 'gesamt'>('lv');
  const [koepfe, setKoepfe] = useState<LvKopf[] | null>(null);
  const [dokument, setDokument] = useState<LvDokument | null>(null);
  const [grundlagen, setGrundlagen] = useState<LvGrundlagen | null>(null);
  const [gesamt, setGesamt] = useState<LvGesamt | null>(null);
  const [gesamtFehler, setGesamtFehler] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState<string | null>(null);

  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<LvPosition | null>(null);
  const [elemente, setElemente] = useState<LvPositionselement[] | null>(null);
  const [suche, setSuche] = useState('');

  useEffect(() => {
    api.lvUebersicht(projekt.id).then(setKoepfe).catch(() => setKoepfe([]));
  }, [api, projekt.id]);

  useEffect(() => {
    setDokument(null);
    setGewaehlt(null);
    setElemente(null);
    setFehler(null);
    if (!fach) return;

    api.lvDokument(projekt.id, gewerk)
      .then((d) => {
        setDokument(d);
        setOffen(new Set(d.titel.slice(0, 1).map((t) => t.oz)));
      })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id, gewerk, fach]);

  const grundlagenLaden = useCallback(() => {
    api.lvGrundlagen(projekt.id, gewerk)
      .then(setGrundlagen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id, gewerk]);

  useEffect(() => {
    if (reiter !== 'grundlagen' || !fach) return;
    grundlagenLaden();
  }, [reiter, fach, grundlagenLaden]);

  // Das Gesamt-LV ist projektweit und nicht an das gewaehlte Gewerk
  // gebunden - deshalb ein eigener Reiter statt einer Filterung des
  // gewerkbezogenen Dokuments.
  const gesamtLaden = useCallback(() => {
    setGesamtFehler(null);
    api.lvGesamt(projekt.id)
      .then(setGesamt)
      .catch((e) => {
        setGesamt(null);
        setGesamtFehler(e instanceof Error ? e.message : String(e));
      });
  }, [api, projekt.id]);

  useEffect(() => {
    if (reiter !== 'gesamt') return;
    gesamtLaden();
  }, [reiter, gesamtLaden]);

  const waehlen = useCallback((p: LvPosition) => {
    setGewaehlt(p);
    setElemente(null);
  }, []);

  const elementeLaden = useCallback(() => {
    if (!gewaehlt) return;
    setElemente([]);
    api.lvPositionselemente(projekt.id, gewerk, gewaehlt.id)
      .then(setElemente)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt.id, gewerk, gewaehlt]);

  const gefiltert = useMemo<LvTitel[]>(() => {
    if (!dokument) return [];
    const s = suche.trim().toLowerCase();
    if (!s) return dokument.titel;

    return dokument.titel
      .map((t) => ({
        ...t,
        positionen: t.positionen.filter(
          (p) => p.oz.toLowerCase().includes(s)
            || p.kurztext.toLowerCase().includes(s)),
      }))
      .filter((t) => t.positionen.length > 0);
  }, [dokument, suche]);

  if (!fach) {
    return (
      <ErrorState
        titel={`Unbekanntes Gewerk: ${gewerk}`}
        meldung={'Die Adresse nennt ein Gewerk, das es in der Ausschreibung nicht gibt. '
          + `Bekannt sind: ${Object.values(GEWERKE).map((g) => g.name).join(', ')}.`}
      />
    );
  }

  const laden = async (art: 'x83' | 'x81' | 'pdf') => {
    if (!dokument) return;
    setLaedt(art);
    setFehler(null);
    try {
      if (art === 'pdf') await api.lvPdf(projekt.id, gewerk, projekt.number);
      else await api.lvGaeb(projekt.id, gewerk, art, projekt.number,
        dokument.kopf.fassung);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(null);
    }
  };

  const kopf = koepfe?.find((k) => k.gewerk === gewerk);

  return (
    <>
      <PageHeader
        titel={`Ausschreibung · ${fach.name}`}
        beschreibung={'Leistungsverzeichnis aus den Mengenregeln, GAEB DA XML 3.3 und '
          + 'PDF im Layout.'}
        meta={kopf
          ? `Fassung ${kopf.fassung} · ${z(kopf.positionen)} Positionen`
          : undefined}
        aktionen={<Link className="m-knopf" to={`../${fach.pfad}`}>
          zurück zu {fach.name}
        </Link>}
      />

      <nav className="reiter" role="tablist" aria-label="Ausschreibungsgewerke"
           style={{ marginBottom: 14 }}>
        {Object.entries(GEWERKE).map(([key, eintrag]) => {
          const k = koepfe?.find((x) => x.gewerk === key);
          return (
            <Link key={key}
                  to={`/projekte/${projekt.id}/ausschreibung/${key}`}
                  role="tab"
                  aria-selected={gewerk === key}
                  className={`reiter-knopf${gewerk === key ? ' aktiv' : ''}`}>
              {eintrag.name}
              {k && k.positionen > 0 && (
                <span className="schwach" style={{ marginLeft: 6, fontSize: 11 }}>
                  {z(k.positionen)}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {fehler && (
        <ErrorState
          meldung="Die Ausgabe des Leistungsverzeichnisses ist gescheitert."
          technisch={fehler}
        />
      )}

      <nav className="reiter" role="tablist" style={{ marginBottom: 14 }}>
        <button role="tab" aria-selected={reiter === 'lv'}
                className={`reiter-knopf${reiter === 'lv' ? ' aktiv' : ''}`}
                onClick={() => setReiter('lv')}>
          Leistungsverzeichnis
        </button>
        <button role="tab" aria-selected={reiter === 'grundlagen'}
                className={`reiter-knopf${reiter === 'grundlagen' ? ' aktiv' : ''}`}
                onClick={() => setReiter('grundlagen')}>
          Grundlagen
        </button>
        <button role="tab" aria-selected={reiter === 'gesamt'}
                className={`reiter-knopf${reiter === 'gesamt' ? ' aktiv' : ''}`}
                onClick={() => setReiter('gesamt')}>
          Gesamt-LV
        </button>
      </nav>

      {reiter === 'gesamt' ? (
        <GesamtLv daten={gesamt} fehler={gesamtFehler} api={api}
                  projektId={projekt.id} projektnummer={projekt.number} />
      ) : reiter === 'grundlagen' ? (
        <Grundlagen daten={grundlagen} api={api} projektId={projekt.id}
                    gewerk={gewerk} neuLaden={grundlagenLaden} />
      ) : !dokument ? (
        <div className="karte">
          <div className="hinweis warnung">
            <IconWarnung size={16} />
            <div>
              Für {fach.name} ist noch kein Leistungsverzeichnis abgeleitet.
              Der Lauf <code>lv-ableiten &lt;projektId&gt; {gewerk}
              {' '}--schreiben</code> erzeugt es aus den Mengenregeln; die
              Regeln selbst stehen im Reiter Grundlagen.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="karte">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                          flexWrap: 'wrap' }}>
              <button className="betont" disabled={laedt !== null}
                      onClick={() => laden('x83')}>
                <IconHerunterladen size={16} />
                {laedt === 'x83' ? 'GAEB wird erzeugt …' : 'GAEB X83 (Angebotsaufforderung)'}
              </button>
              <button disabled={laedt !== null} onClick={() => laden('x81')}>
                <IconHerunterladen size={16} />
                {laedt === 'x81' ? 'GAEB wird erzeugt …' : 'GAEB X81 (Leistungsbeschreibung)'}
              </button>
              <button disabled={laedt !== null} onClick={() => laden('pdf')}>
                <IconHerunterladen size={16} />
                {laedt === 'pdf' ? 'PDF wird erzeugt …' : 'Leistungsverzeichnis als PDF'}
              </button>
              <span className="schwach" style={{ marginLeft: 'auto', fontSize: 12 }}>
                Fassung {dokument.kopf.fassung}
                {dokument.kopf.datenstand
                  ? ` · Datenstand ${new Date(dokument.kopf.datenstand)
                      .toLocaleString('de-DE')}` : ''}
              </span>
            </div>
          </div>

          <div className="karte">
            <div className="kennzahlen">
              <Kennzahl name="Titel" wert={z(dokument.kopf.titel)} />
              <Kennzahl name="Positionen" wert={z(dokument.kopf.positionen)} />
              <Kennzahl name="Modellelemente"
                        wert={z(dokument.kopf.modellelemente)} />
              <Kennzahl name="ohne Position"
                        wert={z(dokument.kopf.elementeOhnePosition)}
                        warnung={dokument.kopf.elementeOhnePosition > 0} />
              <Kennzahl name="Los" wert={`${dokument.kopf.losNr}`} />
            </div>

            {dokument.ohnePosition.length > 0 && (
              <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
                Abnahmeprobe je Bauteilart:{' '}
                {dokument.ohnePosition
                  .map((x) => `${x.bauteilart} ${z(x.anzahl)}`)
                  .join(' · ')}
              </p>
            )}
          </div>

          <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
            {/* Links der Baum Los → Titel → Position, rechts die Position. */}
            <div className="baum">
              <div style={{ padding: '10px 12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IconSuche size={14} />
                  <input
                    value={suche}
                    onChange={(e) => setSuche(e.target.value)}
                    placeholder="OZ oder Kurztext"
                    style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
                  />
                </label>
              </div>

              <div className="baum-abschnitt">
                Los {String(dokument.kopf.losNr).padStart(2, '0')} ·{' '}
                {dokument.kopf.bezeichnung}
              </div>

              {gefiltert.map((t) => (
                <div key={t.oz}>
                  <button
                    className="baum-ebene"
                    onClick={() => setOffen((s) => {
                      const neu = new Set(s);
                      if (neu.has(t.oz)) neu.delete(t.oz); else neu.add(t.oz);
                      return neu;
                    })}
                    title={t.atv ? `${t.bezeichnung} — ${t.atv}` : t.bezeichnung}
                  >
                    <IconEbene size={14} />
                    <span style={{ minWidth: 0, overflow: 'hidden',
                                   textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap' }}>
                      {t.oz} {t.bezeichnung}
                    </span>
                    <span className="schwach">{z(t.positionen.length)}</span>
                  </button>

                  {(offen.has(t.oz) || suche.trim().length > 0)
                    && t.positionen.map((p) => (
                    <button
                      key={p.id}
                      className={`baum-zeile ${gewaehlt?.id === p.id ? 'aktiv' : ''}`}
                      onClick={() => waehlen(p)}
                      title={p.kurztext}
                    >
                      <IconTabelle size={13} />
                      <span style={{ minWidth: 0, overflow: 'hidden',
                                     textOverflow: 'ellipsis',
                                     whiteSpace: 'nowrap' }}>
                        {p.oz}
                        <span className="schwach" style={{ marginLeft: 5 }}>
                          {p.kurztext}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ flex: 1, minWidth: 0, padding: '14px 16px' }}>
              {!gewaehlt ? (
                <div className="leer">
                  Eine Position im Baum wählen.
                </div>
              ) : (
                <Positionsblatt
                  position={gewaehlt}
                  elemente={elemente}
                  elementeLaden={elementeLaden}
                />
              )}
            </div>
          </div>

          <div className="karte">
            <div className="karte-titel">
              <IconBuch size={18} />
              <h3 style={{ margin: 0 }}>Vorbemerkungen</h3>
            </div>
            <table className="zuordnung">
              <thead>
                <tr>
                  <th>Abschnitt</th><th>Gegenstand</th><th>Norm</th>
                  <th>Herkunft</th>
                </tr>
              </thead>
              <tbody>
                {dokument.vorbemerkungen.map((v) => (
                  <tr key={v.id}>
                    <td>{v.abschnitt}</td>
                    <td>{v.titel}</td>
                    <td className="schwach">{v.norm ?? '—'}</td>
                    <td>
                      <Herkunftsmarke herkunft={v.herkunft}
                                      bestaetigt={v.istBestaetigt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="karte">
            <h3 style={{ marginTop: 0 }}>Prüf- und Vergabehinweise</h3>
            <ul style={{ marginBottom: 0, lineHeight: 1.6 }}>
              {dokument.hinweise.map((h) => <li key={h}>{h}</li>)}
            </ul>
          </div>
        </>
      )}

      {kopf && reiter === 'lv' && dokument && (
        <p className="schwach" style={{ fontSize: 11 }}>
          Erzeugt von {kopf.erzeugtVon ?? '—'}
          {kopf.erzeugtAm
            ? ` am ${new Date(kopf.erzeugtAm).toLocaleString('de-DE')}` : ''}
          {kopf.datenhash ? ` · Datenbeleg ${kopf.datenhash.slice(0, 12)}` : ''}
        </p>
      )}
    </>
  );
}

/**
 * Das Gesamt-LV (Schritt L10) — fasst die je Gewerk abgeleiteten Lose zu
 * einem Dokument zusammen. Projektweit und nicht an ein Gewerk gebunden.
 *
 * Es kopiert keine Positionen — die stehen bereits je Los in den anderen
 * Reitern —, sondern zeigt die Zahlen je Los und die zwei Gegenproben, die
 * NUR über die Losgrenze hinweg prüfbar sind: Doppelzuordnung (kein
 * Modellbauteil in mehr als einem Los) und Summenkontrolle (die Summe je
 * Los stimmt mit dem gespeicherten Kopf des Loses überein).
 */
export function GesamtLv({ daten, fehler, api, projektId, projektnummer }: {
  daten: LvGesamt | null;
  fehler: string | null;
  api: ApiClient;
  projektId: string;
  projektnummer: string;
}) {
  const [laedt, setLaedt] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  const laden = async (art: 'x83' | 'x81' | 'pdf') => {
    if (!daten) return;
    setLaedt(art);
    setLadeFehler(null);
    try {
      if (art === 'pdf') await api.lvGesamtPdf(projektId, projektnummer);
      else await api.lvGesamtGaeb(projektId, art, projektnummer, daten.fassung);
    } catch (e) {
      setLadeFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaedt(null);
    }
  };

  if (fehler) {
    return (
      <div className="karte">
        <div className="hinweis warnung">
          <IconWarnung size={16} />
          <div>{fehler}</div>
        </div>
      </div>
    );
  }

  if (!daten) {
    return <div className="leer">Gesamt-LV wird geladen …</div>;
  }

  return (
    <>
      <div className="karte">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <button className="betont" disabled={laedt !== null}
                  onClick={() => laden('x83')}>
            <IconHerunterladen size={16} />
            {laedt === 'x83' ? 'GAEB wird erzeugt …' : 'GAEB X83 (Angebotsaufforderung)'}
          </button>
          <button disabled={laedt !== null} onClick={() => laden('x81')}>
            <IconHerunterladen size={16} />
            {laedt === 'x81' ? 'GAEB wird erzeugt …' : 'GAEB X81 (Leistungsbeschreibung)'}
          </button>
          <button disabled={laedt !== null} onClick={() => laden('pdf')}>
            <IconHerunterladen size={16} />
            {laedt === 'pdf' ? 'PDF wird erzeugt …' : 'Gesamt-LV als PDF-Heft'}
          </button>
          <span className="schwach" style={{ marginLeft: 'auto', fontSize: 12 }}>
            Fassung {daten.fassung}
            {daten.datenstand
              ? ` · Datenstand ${new Date(daten.datenstand)
                  .toLocaleString('de-DE')}` : ''}
          </span>
        </div>
        {ladeFehler && <p className="hinweis fehler">{ladeFehler}</p>}
      </div>

      <div className="karte">
        <div className="kennzahlen">
          <Kennzahl name="Lose" wert={`${daten.zahlen.lose} von ${daten.loseGesamt}`} />
          <Kennzahl name="Titel" wert={z(daten.zahlen.titel)} />
          <Kennzahl name="Positionen" wert={z(daten.zahlen.positionen)} />
          <Kennzahl name="Modellelemente" wert={z(daten.zahlen.modellelemente)} />
          <Kennzahl name="ohne Position" wert={z(daten.zahlen.elementeOhnePosition)}
                    warnung={daten.zahlen.elementeOhnePosition > 0} />
        </div>

        {daten.fehlendeLose.length > 0 && (
          <div className="hinweis warnung" style={{ marginTop: 10 }}>
            <IconWarnung size={16} />
            <div>
              Noch nicht abgeleitete Lose (fehlen in diesem Dokument):{' '}
              {daten.fehlendeLose.join(', ')}. Der Lauf{' '}
              <code>lv-ableiten &lt;projektId&gt; gesamt --schreiben</code>{' '}
              fasst die bereits abgeleiteten Lose zusammen, sobald mindestens
              eines vorliegt.
            </div>
          </div>
        )}
      </div>

      <div className="karte">
        <div className="karte-titel">
          <IconTabelle size={18} />
          <h3 style={{ margin: 0 }}>Zahlen je Los</h3>
        </div>
        <table className="zuordnung">
          <thead>
            <tr>
              <th>Los</th><th>Bezeichnung</th><th>Fassung</th>
              <th style={{ textAlign: 'right' }}>Titel</th>
              <th style={{ textAlign: 'right' }}>Positionen</th>
              <th style={{ textAlign: 'right' }}>Modellelemente</th>
              <th style={{ textAlign: 'right' }}>ohne Position</th>
            </tr>
          </thead>
          <tbody>
            {daten.lose.map((l) => (
              <tr key={l.gewerk}>
                <td>{String(l.losNr).padStart(2, '0')}</td>
                <td>
                  <Link to={`/projekte/${projektId}/ausschreibung/${l.gewerk}`}>
                    {l.bezeichnung}
                  </Link>
                </td>
                <td className="schwach">{l.fassung}</td>
                <td style={{ textAlign: 'right' }}>{z(l.titel)}</td>
                <td style={{ textAlign: 'right' }}>{z(l.positionen)}</td>
                <td style={{ textAlign: 'right' }}>{z(l.modellelemente)}</td>
                <td style={{ textAlign: 'right',
                             color: l.elementeOhnePosition > 0
                               ? 'var(--warnung)' : undefined }}>
                  {z(l.elementeOhnePosition)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="karte">
        <div className="karte-titel">
          <IconOk size={18} />
          <h3 style={{ margin: 0 }}>Gegenproben über die Losgrenze hinweg</h3>
        </div>
        <p className="schwach" style={{ fontSize: 12 }}>
          Diese zwei Prüfungen sind nur über die Losgrenze hinweg möglich: Der
          eindeutige Index je Los verhindert Doppelzuordnungen nicht
          gewerkübergreifend, und die Summenkontrolle prüft den Zusammenlauf
          selbst gegen den gespeicherten Kopf jedes Loses.
        </p>
        <ul style={{ fontSize: 13, lineHeight: 1.6 }}>
          {daten.gegenproben.map((g) => (
            <li key={g.schluessel}>
              <StatusBadge
                klein
                zustand={g.ergebnis === 'fehler' ? 'fehler'
                  : g.ergebnis === 'hinweis' ? 'warnung' : 'gut'}
                text={g.ergebnis === 'fehler' ? 'Fehler'
                  : g.ergebnis === 'hinweis' ? 'Hinweis' : 'in Ordnung'}
              />{' '}{g.aussage}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** Die gewählte Position mit allem, was an ihr hängt. */
function Positionsblatt({ position, elemente, elementeLaden }: {
  position: LvPosition;
  elemente: LvPositionselement[] | null;
  elementeLaden: () => void;
}) {
  const herkunft = HERKUNFT[position.mengenherkunft]
    ?? { text: position.mengenherkunft, zustand: 'neutral' as Zustand };
  const art = POSITIONSART[position.positionsart] ?? position.positionsart;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                    flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>{position.oz}</h3>
        <span style={{ fontWeight: 600 }}>{position.kurztext}</span>
        {art && (
          <span className="marke" style={{ background: 'var(--warnung)' }}>
            {art}
          </span>
        )}
      </div>

      <div className="kennzahlen" style={{ marginTop: 12 }}>
        <Kennzahl
          name={`Menge (${position.einheit})`}
          wert={position.menge.toLocaleString('de-DE', {
            minimumFractionDigits: position.dezimalstellen,
            maximumFractionDigits: position.dezimalstellen,
          })}
        />
        <Kennzahl name="Modellelemente" wert={z(position.modellelemente)} />
      </div>

      <p style={{ marginTop: 10 }}>
        <span className="schwach">Mengenherkunft: </span>
        <StatusBadge klein zustand={herkunft.zustand} text={herkunft.text} />
        {position.regel && (
          <span className="schwach"> · Regel {position.regel}</span>
        )}
        {position.textbaustein && (
          <span className="schwach"> · Textbaustein {position.textbaustein}</span>
        )}
      </p>

      {position.hinweis && (
        <div className="hinweis warnung" style={{ marginBottom: 10 }}>
          <IconWarnung size={16} />
          <div>{position.hinweis}</div>
        </div>
      )}

      <h4 style={{ marginBottom: 4 }}>Langtext</h4>
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: 13,
                    borderLeft: '3px solid var(--rand)', paddingLeft: 10 }}>
        {position.langtext}
      </div>

      {position.nachweis && (
        <>
          <h4 style={{ marginBottom: 4 }}>Nachweis mit dem Angebot</h4>
          <p style={{ fontSize: 13 }}>{position.nachweis}</p>
        </>
      )}

      {position.bieterangaben && (
        <>
          <h4 style={{ marginBottom: 4 }}>Bieterangabe</h4>
          <p style={{ fontSize: 13, fontFamily: 'monospace' }}>
            {position.bieterangaben}
          </p>
        </>
      )}

      <h4 style={{ marginBottom: 4 }}>Modellelemente</h4>
      {position.modellelemente === 0 ? (
        <p className="schwach" style={{ fontSize: 13 }}>
          Diese Position hängt an keinem Modellbauteil — ihre Menge ist eine{' '}
          {herkunft.text}.
        </p>
      ) : elemente === null ? (
        <button onClick={elementeLaden}>
          {z(position.modellelemente)} Modellelemente anzeigen
        </button>
      ) : (
        /* Umzug U6c: `DataTable` virtualisiert ab 200 Zeilen — das
           `.slice(0, 200)` mit dem Satz „Die ersten 200 von …" entfällt
           ersatzlos. */
        <DataTable
          name="Modellelemente"
          spalten={ELEMENT_SPALTEN}
          zeilen={elemente}
          schluessel={(e) => `${e.quelltabelle}/${e.quellId}`}
          dicht
          ausfuhr
          ausfuhrDateiname={`lv-${position.oz}-modellelemente`}
          leer={(
            <EmptyState
              klein
              titel="Zu dieser Position ist kein Modellelement gespeichert."
              erklaerung={'Die Position zählt Modellelemente, der Lauf hat aber keine '
                + 'hinterlegt — das ist ein Befund und kein Ergebnis.'}
            />
          )}
        />
      )}
    </>
  );
}

const ELEMENT_SPALTEN: Spalte<LvPositionselement>[] = [
  { schluessel: 'quelltabelle', titel: 'Quelle', wert: (e) => e.quelltabelle },
  {
    schluessel: 'quellId', titel: 'Kennung', immerSichtbar: true,
    wert: (e) => e.quellId,
    inhalt: (e) => (
      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{e.quellId}</span>
    ),
  },
  { schluessel: 'leistungsart', titel: 'Leistungsart', wert: (e) => e.leistungsart },
  { schluessel: 'mengeAnteil', titel: 'Anteil', art: 'zahl', nachkomma: 3,
    wert: (e) => e.mengeAnteil },
];

/**
 * Die Grundlagen: Textbausteine, Vorbemerkungen, Mengenregeln, Einheiten.
 *
 * Mengenregeln stehen hier zum LESEN. Eine Regel ändert die MENGE jeder
 * Position, die sie erzeugt — das gehört in einen Lauf mit Zahlenvergleich
 * und nicht hinter einen Speichernknopf.
 */
export function Grundlagen({ daten, api, projektId, gewerk, neuLaden }: {
  daten: LvGrundlagen | null;
  api: ApiClient;
  projektId: string;
  gewerk: string;
  neuLaden: () => void;
}) {
  const [lauf, setLauf] = useState<LvLauf | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [laufFehler, setLaufFehler] = useState<string | null>(null);
  const [baustein, setBaustein] = useState<LvTextbaustein | null>(null);
  const [vorbemerkung, setVorbemerkung] = useState<LvVorbemerkung | null>(null);

  const probelauf = useCallback(() => {
    setLaeuft(true);
    setLaufFehler(null);
    api.lvProbelauf(projektId, gewerk)
      .then(setLauf)
      .catch((e) => setLaufFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setLaeuft(false));
  }, [api, projektId, gewerk]);

  if (!daten) return <div className="leer">Grundlagen werden geladen …</div>;

  const unbestaetigt = daten.textbausteine.filter((t) => !t.istBestaetigt).length;
  const vorbUnbestaetigt =
    daten.vorbemerkungen.filter((v) => !v.istBestaetigt).length;

  return (
    <>
      <div className="karte">
        <div className="kennzahlen">
          <Kennzahl name="Textbausteine" wert={z(daten.textbausteine.length)} />
          <Kennzahl name="davon Vorlage" wert={z(unbestaetigt)}
                    warnung={unbestaetigt > 0} />
          <Kennzahl name="Vorbemerkungen" wert={z(daten.vorbemerkungen.length)} />
          <Kennzahl name="davon Vorlage" wert={z(vorbUnbestaetigt)}
                    warnung={vorbUnbestaetigt > 0} />
          <Kennzahl name="Mengenregeln" wert={z(daten.mengenregeln.length)} />
          <Kennzahl name="Merkmalregeln"
                    wert={z(daten.merkmalregeln?.length ?? 0)} />
          <Kennzahl name="Einheiten" wert={z(daten.einheiten.length)} />
        </div>
      </div>

      {/*
        DER PROBELAUF.

        Eine Mengenregel ändert die MENGE jeder Position, die sie erzeugt —
        und damit den Preis. Deshalb gibt es auf dieser Seite keinen
        Speichernknopf für sie, sondern diesen Weg: rechnen, die Zahlen
        vorher und nachher nebeneinander legen, dann entscheiden.
      */}
      <div className="karte">
        <div className="karte-titel">
          <IconTabelle size={18} />
          <h3 style={{ margin: 0 }}>Lauf mit Zahlenvergleich</h3>
        </div>
        <p className="schwach" style={{ fontSize: 12 }}>
          Der Probelauf rechnet das ganze Leistungsverzeichnis neu und
          schreibt nichts. Er zeigt je Titel und je Mengeneinheit, was heute
          im Dokument steht und was daraus würde.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="knopf betont" onClick={probelauf} disabled={laeuft}>
            {laeuft ? 'Rechnet …' : 'Probelauf'}
          </button>
          {lauf && (
            <span className={lauf.hatGeaendert ? '' : 'schwach'}>
              {lauf.hatGeaendert
                ? `Der Lauf WÜRDE das Dokument ändern (Fassung ${lauf.fassung} → ${lauf.fassung + 1}).`
                : `Keine Änderung — Fassung bleibt ${lauf.fassung}.`}
            </span>
          )}
        </div>
        {laufFehler && <p className="hinweis fehler">{laufFehler}</p>}

        {lauf && (
          <>
            <table className="zuordnung" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Titel</th><th>Einheit</th>
                  <th style={{ textAlign: 'right' }}>Positionen vorher</th>
                  <th style={{ textAlign: 'right' }}>nachher</th>
                  <th style={{ textAlign: 'right' }}>Menge vorher</th>
                  <th style={{ textAlign: 'right' }}>nachher</th>
                </tr>
              </thead>
              <tbody>
                {lauf.vergleich.map((v) => (
                  <tr key={`${v.titelOz}-${v.einheit}`}
                      style={{ fontWeight: v.aendert ? 600 : undefined }}>
                    <td>{v.titelOz} {v.bezeichnung}</td>
                    <td>{v.einheit}</td>
                    <td style={{ textAlign: 'right' }}>{z(v.positionenVorher)}</td>
                    <td style={{ textAlign: 'right',
                                 color: v.positionenVorher !== v.positionenNachher
                                   ? 'var(--warnung)' : undefined }}>
                      {z(v.positionenNachher)}
                    </td>
                    <td style={{ textAlign: 'right' }}>{z(v.mengeVorher, 3)}</td>
                    <td style={{ textAlign: 'right',
                                 color: v.mengeVorher !== v.mengeNachher
                                   ? 'var(--warnung)' : undefined }}>
                      {z(v.mengeNachher, 3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={{ marginBottom: 4 }}>Gegenproben</h4>
            <ul style={{ fontSize: 12 }}>
              {lauf.gegenproben.map((g) => (
                <li key={g.schluessel}>
                  <StatusBadge
                    klein
                    zustand={g.ergebnis === 'fehler' ? 'fehler'
                      : g.ergebnis === 'hinweis' ? 'warnung' : 'gut'}
                    text={g.ergebnis === 'fehler' ? 'Fehler'
                      : g.ergebnis === 'hinweis' ? 'Hinweis' : 'in Ordnung'}
                  />{' '}{g.aussage}
                </li>
              ))}
            </ul>

            {lauf.hinweise.length > 0 && (
              <details>
                <summary className="schwach" style={{ fontSize: 12 }}>
                  {z(lauf.hinweise.length)} Hinweise des Laufs
                </summary>
                <ul className="schwach" style={{ fontSize: 11 }}>
                  {lauf.hinweise.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              </details>
            )}
          </>
        )}
      </div>

      <div className="karte">
        <div className="karte-titel">
          <IconBuch size={18} />
          <h3 style={{ margin: 0 }}>Vorbemerkungen</h3>
        </div>
        <table className="zuordnung">
          <thead>
            <tr>
              <th>Abschnitt</th><th>Titel</th><th>Norm</th>
              <th>Herkunft</th><th>bestätigt am</th><th></th>
            </tr>
          </thead>
          <tbody>
            {daten.vorbemerkungen.map((v) => (
              <tr key={v.id}>
                <td style={{ fontFamily: 'monospace' }}>{v.abschnitt}</td>
                <td title={v.text}>{v.titel}</td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {v.norm ?? '—'}
                </td>
                <td>
                  <Herkunftsmarke herkunft={v.herkunft}
                                  bestaetigt={v.istBestaetigt} />
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {datum(v.bestaetigtAm)}
                </td>
                <td>
                  <button className="knopf-mager"
                          onClick={() => setVorbemerkung(v)}>
                    Pflegen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="karte">
        <div className="karte-titel">
          <IconBuch size={18} />
          <h3 style={{ margin: 0 }}>Textbausteine</h3>
        </div>
        <table className="zuordnung">
          <thead>
            <tr>
              <th>Schlüssel</th><th>Kurztext</th><th>Einheit</th>
              <th>Platzhalter</th><th>Bieterangabe</th>
              <th>Herkunft</th><th>bestätigt am</th><th></th>
            </tr>
          </thead>
          <tbody>
            {daten.textbausteine.map((t) => (
              <tr key={t.id}
                  style={{ opacity: t.istAktiv === false ? 0.45 : undefined }}>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {t.schluessel}
                  {t.istAktiv === false && ' (abgeschaltet)'}
                </td>
                <td title={t.langtext}>{t.kurztext}</td>
                <td>{t.einheitKuerzel ?? '—'}</td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {t.felder.length > 0 ? t.felder.join(' ') : '—'}
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {t.bieterangaben ?? '—'}
                </td>
                <td>
                  <Herkunftsmarke herkunft={t.herkunft}
                                  bestaetigt={t.istBestaetigt} />
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {datum(t.bestaetigtAm)}
                </td>
                <td>
                  <button className="knopf-mager" onClick={() => setBaustein(t)}>
                    Pflegen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="karte">
        <div className="karte-titel">
          <IconTabelle size={18} />
          <h3 style={{ margin: 0 }}>Mengenregeln</h3>
        </div>
        <p className="schwach" style={{ fontSize: 12 }}>
          Eine Regel ändert die Menge jeder Position, die sie erzeugt. Sie wird
          deshalb nicht auf dieser Seite geändert, sondern über den Lauf mit
          Zahlenvergleich oben.
        </p>
        <table className="zuordnung">
          <thead>
            <tr>
              <th>Titel</th><th>Schlüssel</th><th>Quelle</th>
              <th>Filter</th><th>Gruppierung</th><th>Aggregation</th>
              <th>Einheit</th>
              <th style={{ textAlign: 'right' }}>Mindestmenge</th>
              <th>Mengenherkunft</th><th>Herkunft</th><th>bestätigt am</th>
            </tr>
          </thead>
          <tbody>
            {daten.mengenregeln.map((r) => (
              <tr key={r.id}
                  style={{ opacity: r.istAktiv === false ? 0.45 : undefined }}>
                <td>{r.titelOz} {r.titelBezeichnung}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}
                    title={r.begruendung ?? undefined}>
                  {r.schluessel}
                  {r.istAktiv === false && ' (abgeschaltet)'}
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>{r.quelle}</td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {r.filterJson ?? '—'}
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {r.gruppierungJson ?? '—'}
                </td>
                <td>{r.aggregation}</td>
                <td>{r.einheitKuerzel}</td>
                <td style={{ textAlign: 'right' }}>
                  {r.mindestmenge !== null
                    ? r.mindestmenge.toLocaleString('de-DE')
                    : r.festmenge !== null
                      ? `fest ${r.festmenge.toLocaleString('de-DE')}`
                      : '—'}
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {r.mengenherkunft}
                </td>
                <td>
                  <Herkunftsmarke herkunft={r.herkunft}
                                  bestaetigt={r.istBestaetigt} />
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {datum(r.bestaetigtAm)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        DIE MERKMALREGELN.

        Sie beantworten die zwei Fragen, an denen das Referenz-LV gescheitert
        ist: Was IST "TROX RN-D 80" (M-4), und wozu dient "Antidröhnmatten
        50mm" (M-6). Ein Muster ohne Treffer lässt das Merkmal LEER — dann
        entfällt die Textzeile, statt einen falschen Wert zu behaupten.
      */}
      {daten.merkmalregeln && daten.merkmalregeln.length > 0 && (
        <div className="karte">
          <div className="karte-titel">
            <IconTabelle size={18} />
            <h3 style={{ margin: 0 }}>Merkmalregeln</h3>
          </div>
          <p className="schwach" style={{ fontSize: 12 }}>
            Aus einem Produktnamen des Modells wird eine Leistung. Die erste
            zutreffende Regel gewinnt; trifft keine, bleibt das Merkmal leer
            und die zugehörige Textzeile entfällt.
          </p>
          <table className="zuordnung">
            <thead>
              <tr>
                <th>Merkmal</th><th>Feld</th><th>Vergleich</th><th>Muster</th>
                <th>Wert</th><th>Norm</th><th>Herkunft</th>
              </tr>
            </thead>
            <tbody>
              {daten.merkmalregeln.map((m) => (
                <tr key={m.id}>
                  <td>{m.merkmal}</td>
                  <td className="schwach" style={{ fontSize: 11 }}>
                    {m.quellfeld}
                  </td>
                  <td className="schwach" style={{ fontSize: 11 }}>
                    {m.vergleich}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {m.muster}
                  </td>
                  <td>{m.wert}</td>
                  <td className="schwach" style={{ fontSize: 11 }}>
                    {m.norm ?? '—'}
                  </td>
                  <td>
                    <Herkunftsmarke herkunft={m.herkunft}
                                    bestaetigt={m.istBestaetigt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="karte">
        <div className="karte-titel">
          <IconTabelle size={18} />
          <h3 style={{ margin: 0 }}>Mengeneinheiten (GAEB-Katalog)</h3>
        </div>
        <table className="zuordnung">
          <thead>
            <tr>
              <th>Kürzel</th><th>Bezeichnung</th><th>Art</th>
              <th style={{ textAlign: 'right' }}>Stellen</th>
              <th>am Katalog belegt</th><th>Quelle</th>
            </tr>
          </thead>
          <tbody>
            {daten.einheiten.map((e) => (
              <tr key={e.kuerzel}>
                <td style={{ fontFamily: 'monospace' }}>{e.kuerzel}</td>
                <td>{e.bezeichnung}</td>
                <td className="schwach">{e.art}</td>
                <td style={{ textAlign: 'right' }}>{e.dezimalstellen}</td>
                <td>
                  {e.istGaebKatalog
                    ? <StatusBadge klein zustand="gut" text="belegt"
                        titel="Im GAEB-Einheitenkatalog belegt" />
                    : <StatusBadge klein zustand="neutral" text="üblich"
                        titel="Nicht im GAEB-Katalog — übliche Schreibweise" />}
                </td>
                <td className="schwach" style={{ fontSize: 11 }}>
                  {e.quelle ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {baustein && (
        <Bausteinpflege baustein={baustein} api={api} projektId={projektId}
                        gewerk={gewerk}
                        schliessen={() => setBaustein(null)}
                        fertig={() => { setBaustein(null); neuLaden(); }} />
      )}
      {vorbemerkung && (
        <Vorbemerkungspflege vorbemerkung={vorbemerkung} api={api}
                             projektId={projektId} gewerk={gewerk}
                             schliessen={() => setVorbemerkung(null)}
                             fertig={() => { setVorbemerkung(null); neuLaden(); }} />
      )}
    </>
  );
}

/**
 * Das Pflegeformular eines Textbausteins.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS HIER NICHT ÄNDERBAR IST
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Schlüssel, Einheit und Positionsart. Sie hängen an der Mengenregel; wer sie
 * hier ändern könnte, würde die Menge ändern, ohne dass ein Lauf
 * stattgefunden hat.
 *
 * Gespeichert wird IMMER als projektbezogene Zeile. Die Katalogvorlage bleibt
 * stehen — sie ist der Maßstab, an dem sich ablesen lässt, WAS geändert
 * wurde.
 */
function Bausteinpflege({ baustein, api, projektId, gewerk, schliessen, fertig }: {
  baustein: LvTextbaustein;
  api: ApiClient;
  projektId: string;
  gewerk: string;
  schliessen: () => void;
  fertig: () => void;
}) {
  const [kurztext, setKurztext] = useState(baustein.kurztext);
  const [langtext, setLangtext] = useState(baustein.langtext);
  const [normen, setNormen] = useState(baustein.normverweise ?? '');
  const [nachweis, setNachweis] = useState(baustein.nachweis ?? '');
  const [bieter, setBieter] = useState(baustein.bieterangaben ?? '');
  const [bestaetigt, setBestaetigt] = useState(baustein.istBestaetigt);
  const [begruendung, setBegruendung] = useState(baustein.begruendung ?? '');
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const zuLang = kurztext.length > 70;

  const speichern = () => {
    setSpeichert(true);
    setFehler(null);
    api.lvTextbausteinPflegen(projektId, gewerk, baustein.schluessel, {
      kurztext, langtext,
      normverweise: normen.trim() === '' ? null : normen,
      nachweis: nachweis.trim() === '' ? null : nachweis,
      bieterangaben: bieter.trim() === '' ? null : bieter,
      istBestaetigt: bestaetigt,
      begruendung: begruendung.trim() === '' ? null : begruendung,
    })
      .then(() => fertig())
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setSpeichert(false));
  };

  return (
    <div className="karte" style={{ borderColor: 'var(--akzent)' }}>
      <div className="karte-titel">
        <IconBuch size={18} />
        <h3 style={{ margin: 0 }}>Textbaustein {baustein.schluessel}</h3>
      </div>

      <label className="feld">
        <span className="feldname">Kurztext (höchstens 70 Zeichen — GAEB-Grenze)</span>
        <input value={kurztext} onChange={(e) => setKurztext(e.target.value)} />
        <small className={zuLang ? 'hinweis fehler' : 'schwach'}>
          {kurztext.length} Zeichen
          {zuLang && ' — zu lang, der Export würde ihn kürzen'}
        </small>
      </label>

      <label className="feld">
        <span className="feldname">Langtext</span>
        <textarea rows={14} value={langtext}
                  onChange={(e) => setLangtext(e.target.value)} />
        <small className="schwach">
          Platzhalter in geschweiften Klammern. Verfügbar in dieser Vorlage:{' '}
          {baustein.felder.length > 0 ? baustein.felder.join(' ') : '—'}. Ein
          Platzhalter ohne Wert löscht SEINE ZEILE.
        </small>
      </label>

      <label className="feld">
        <span className="feldname">Normverweise</span>
        <input value={normen} onChange={(e) => setNormen(e.target.value)} />
      </label>

      <label className="feld">
        <span className="feldname">Nachweis mit dem Angebot</span>
        <input value={nachweis} onChange={(e) => setNachweis(e.target.value)} />
      </label>

      <label className="feld">
        <span className="feldname">Bieterangaben — mehrere Felder mit Semikolon trennen</span>
        <input value={bieter} onChange={(e) => setBieter(e.target.value)} />
        <small className="schwach">
          Jeder Eintrag wird im GAEB ein eigenes Eintragefeld des Bieters
          (TextComplement, Kind=&quot;Bidder&quot;).
        </small>
      </label>

      <label className="feld" style={{ flexDirection: 'row', gap: 8,
                                       alignItems: 'center' }}>
        <input type="checkbox" checked={bestaetigt}
               onChange={(e) => setBestaetigt(e.target.checked)} />
        <span className="feldname">Vom Fachplaner bestätigt</span>
      </label>

      <label className="feld">
        <span className="feldname">Begründung — womit wurde entschieden</span>
        <textarea rows={3} value={begruendung}
                  onChange={(e) => setBegruendung(e.target.value)} />
      </label>

      {fehler && <p className="hinweis fehler">{fehler}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="knopf" onClick={speichern}
                disabled={speichert || zuLang}>
          {speichert ? 'Speichert …' : 'Speichern'}
        </button>
        <button className="knopf" onClick={schliessen}>Abbrechen</button>
      </div>
    </div>
  );
}

/** Das Pflegeformular einer Vorbemerkung. */
function Vorbemerkungspflege({
  vorbemerkung, api, projektId, gewerk, schliessen, fertig,
}: {
  vorbemerkung: LvVorbemerkung;
  api: ApiClient;
  projektId: string;
  gewerk: string;
  schliessen: () => void;
  fertig: () => void;
}) {
  const [titel, setTitel] = useState(vorbemerkung.titel);
  const [text, setText] = useState(vorbemerkung.text);
  const [norm, setNorm] = useState(vorbemerkung.norm ?? '');
  const [bestaetigt, setBestaetigt] = useState(vorbemerkung.istBestaetigt);
  const [begruendung, setBegruendung] =
    useState(vorbemerkung.begruendung ?? '');
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const speichern = () => {
    setSpeichert(true);
    setFehler(null);
    api.lvVorbemerkungPflegen(projektId, gewerk, vorbemerkung.abschnitt, {
      titel, text,
      norm: norm.trim() === '' ? null : norm,
      istBestaetigt: bestaetigt,
      begruendung: begruendung.trim() === '' ? null : begruendung,
    })
      .then(() => fertig())
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setSpeichert(false));
  };

  return (
    <div className="karte" style={{ borderColor: 'var(--akzent)' }}>
      <div className="karte-titel">
        <IconBuch size={18} />
        <h3 style={{ margin: 0 }}>
          Vorbemerkung {vorbemerkung.abschnitt}
        </h3>
      </div>

      <label className="feld">
        <span className="feldname">Titel</span>
        <input value={titel} onChange={(e) => setTitel(e.target.value)} />
      </label>

      <label className="feld">
        <span className="feldname">Text</span>
        <textarea rows={12} value={text}
                  onChange={(e) => setText(e.target.value)} />
      </label>

      <label className="feld">
        <span className="feldname">Norm</span>
        <input value={norm} onChange={(e) => setNorm(e.target.value)} />
      </label>

      <label className="feld" style={{ flexDirection: 'row', gap: 8,
                                       alignItems: 'center' }}>
        <input type="checkbox" checked={bestaetigt}
               onChange={(e) => setBestaetigt(e.target.checked)} />
        <span className="feldname">Vom Fachplaner bestätigt</span>
      </label>

      <label className="feld">
        <span className="feldname">Begründung — womit wurde entschieden</span>
        <textarea rows={3} value={begruendung}
                  onChange={(e) => setBegruendung(e.target.value)} />
      </label>

      {fehler && <p className="hinweis fehler">{fehler}</p>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="knopf" onClick={speichern} disabled={speichert}>
          {speichert ? 'Speichert …' : 'Speichern'}
        </button>
        <button className="knopf" onClick={schliessen}>Abbrechen</button>
      </div>
    </div>
  );
}

/** Ein Datum, oder ein Strich. */
function datum(wert: string | null | undefined) {
  if (!wert) return '—';
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
}

function Herkunftsmarke({ herkunft, bestaetigt }: {
  herkunft: string; bestaetigt: boolean;
}) {
  if (bestaetigt) {
    return (
      <StatusBadge klein zustand="gut"
        text={herkunft === 'fachplaner' ? 'Fachplaner' : herkunft} />
    );
  }
  return (
    <StatusBadge klein zustand="neutral"
      text={herkunft === 'vorlage' ? 'Vorlage, unbestätigt' : herkunft} />
  );
}

function Kennzahl({ name, wert, warnung }: {
  name: string; wert: string; warnung?: boolean;
}) {
  return (
    <div className="kennzahl">
      <div className="kennzahl-wert"
           style={{ color: warnung ? 'var(--warnung)' : undefined }}>{wert}</div>
      <div className="kennzahl-name">{name}</div>
    </div>
  );
}

function z(n: number, stellen = 0) {
  return n.toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });
}
